import React, { useState } from 'react'
import { useIntl } from 'react-intl'

import { Typography } from '@s4l/ds'
import Input from 'src/comps/Atom/Inputs/Input/Input'
import SubmitButton from '../SubmitButton/index'

import { validateEmail, validateName } from 'client/helpers/Form'
import type { InvoiceFormData } from '../types'
import styles from './index.module.css'

interface FormFieldsProps {
  formData: InvoiceFormData
  onFieldChange: (value: string, fieldName: keyof InvoiceFormData) => void
  onSubmit: (e: React.FormEvent) => void
  children?: React.ReactNode
}

type ErrorsType = {
  name?: ReturnType<typeof validateName>
  address?: ReturnType<typeof validateName>
  vatNumber?: ReturnType<typeof validateName>
  email?: ReturnType<typeof validateEmail>
}

const FORM_DATA_LANG_IDS_MAP = {
  name: 'input.name-or-company-name',
  address: 'address',
  vatNumber: 'global.vat-number',
  email: 'global.confirm-email',
}

const ERRORS_LANG_IDS_MAP = {
  required: 'validation.required',
  pleaseEmail: 'subscription.please-enter-your-email',
}

const FormFields: React.FC<FormFieldsProps> = ({ formData, onFieldChange, onSubmit, children }) => {
  const intl = useIntl()
  const [errors, setErrors] = useState<ErrorsType>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  function validateField(fieldName: keyof InvoiceFormData, value: string): boolean {
    const rules = {
      required: true,
    }
    const error =
      fieldName === 'email' ? validateEmail(value, rules) : validateName({ name: value, rules })
    setErrors({
      ...errors,
      [fieldName]: error,
    })
    return !error
  }

  function validateForm(): boolean {
    const nextErrors = {
      name: validateName({ name: formData.name, rules: { required: true } }),
      address: validateName({ name: formData.address, rules: { required: true } }),
      vatNumber: validateName({ name: formData.vatNumber, rules: { required: true } }),
      email: validateEmail(formData.email, { required: true }),
    }
    setErrors(nextErrors)
    const hasErrors = Object.values(nextErrors).some(Boolean)
    return !hasErrors
  }

  function handdleInputChange(value: string, fieldName: keyof InvoiceFormData) {
    onFieldChange(value, fieldName)
    validateField(fieldName, value)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (validateForm()) {
      setIsSubmitting(true)
      onSubmit(e)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      {Object.keys(formData).map(key => (
        <div key={key} className={key === 'email' ? styles.lastFormGroup : styles.formGroup}>
          <Input
            name={key}
            value={formData[key as keyof InvoiceFormData]}
            onChange={value => handdleInputChange(value, key as keyof InvoiceFormData)}
            onBlur={() =>
              validateField(key as keyof InvoiceFormData, formData[key as keyof InvoiceFormData])
            }
            label={intl.formatMessage({
              id: FORM_DATA_LANG_IDS_MAP[key as keyof InvoiceFormData],
            })}
            errors={errors[key as keyof InvoiceFormData]?.slice(0, 1)?.map(error =>
              intl.formatMessage({
                id: ERRORS_LANG_IDS_MAP[error.id as keyof typeof ERRORS_LANG_IDS_MAP],
              })
            )}
          />
        </div>
      ))}

      <div className={styles.emailNote}>
        <Typography variant="body" size="m" as="p">
          {intl.formatMessage({
            id: 'confirm-email.assistive-text',
          })}
        </Typography>
      </div>

      {children}

      <SubmitButton disabled={isSubmitting} />
    </form>
  )
}

export default FormFields
